import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Quote,
  Redo2,
  Undo2,
} from 'lucide-react';
import { useEffect, type ReactNode } from 'react';

import { cn } from '@/utils/cn';

/**
 * Constrained rich text for devotionals, announcements and project stories.
 *
 * The feature set is deliberately fixed — headings, bold, italic, lists, quote, link,
 * image — because this content is also rendered in the Flutter app and in generated
 * PDFs. Anything the editor can produce, those two have to be able to draw. An open
 * editor with colours and font sizes would mean a devotional that looks correct here
 * and broken on a phone.
 */

export interface RichTextEditorProps {
  /** HTML. Round-trips through the app and the PDF renderer unchanged. */
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  /** Opens the church's media picker; resolves to a URL to insert. */
  onRequestImage?: () => Promise<string | null>;
  /** Prompts for a URL through a project dialog — never `window.prompt`. */
  onRequestLink?: (current: string | null) => Promise<string | null>;
  labels: {
    bold: string;
    italic: string;
    heading2: string;
    heading3: string;
    bulletList: string;
    orderedList: string;
    quote: string;
    link: string;
    image: string;
    undo: string;
    redo: string;
    toolbar: string;
  };
  minHeight?: number;
  className?: string;
  'aria-describedby'?: string;
}

export const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  invalid = false,
  onRequestImage,
  onRequestLink,
  labels,
  minHeight = 200,
  className,
  ...aria
}: RichTextEditorProps) => {
  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        // The document has one h1 — the page title — so the editor starts at h2.
        codeBlock: false,
        horizontalRule: false,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        // Church content is often shared publicly; links out of it should not be able
        // to reach back into the page that opened them.
        HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' },
      }),
      Image.configure({ HTMLAttributes: { class: 'rounded-md' } }),
      Placeholder.configure({ placeholder: placeholder ?? '' }),
    ],
    content: value,
    onUpdate: ({ editor: instance }) => onChange(instance.getHTML()),
    editorProps: {
      attributes: {
        class: 'prose-trinity focus:outline-none',
        'aria-describedby': aria['aria-describedby'] ?? '',
      },
    },
  });

  // External changes — a loaded draft, a discarded edit — are pushed in, but only when
  // they differ, or every keystroke would reset the cursor to the start.
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() === value) return;
    editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) return null;

  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border bg-surface',
        invalid ? 'border-danger' : 'border-border focus-within:border-brand',
        disabled && 'opacity-60',
        className,
      )}
    >
      <div
        role="toolbar"
        aria-label={labels.toolbar}
        className="flex flex-wrap items-center gap-0.5 border-b border-border bg-surface-sunken px-1.5 py-1"
      >
        <ToolbarButton
          label={labels.bold}
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
          icon={<Bold className="h-4 w-4" />}
        />
        <ToolbarButton
          label={labels.italic}
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          icon={<Italic className="h-4 w-4" />}
        />

        <Divider />

        <ToolbarButton
          label={labels.heading2}
          active={editor.isActive('heading', { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          icon={<Heading2 className="h-4 w-4" />}
        />
        <ToolbarButton
          label={labels.heading3}
          active={editor.isActive('heading', { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          icon={<Heading3 className="h-4 w-4" />}
        />

        <Divider />

        <ToolbarButton
          label={labels.bulletList}
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          icon={<List className="h-4 w-4" />}
        />
        <ToolbarButton
          label={labels.orderedList}
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          icon={<ListOrdered className="h-4 w-4" />}
        />
        <ToolbarButton
          label={labels.quote}
          active={editor.isActive('blockquote')}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          icon={<Quote className="h-4 w-4" />}
        />

        {onRequestLink || onRequestImage ? <Divider /> : null}

        {onRequestLink ? (
          <ToolbarButton
            label={labels.link}
            active={editor.isActive('link')}
            onClick={async () => {
              const current = (editor.getAttributes('link').href as string) ?? null;
              const href = await onRequestLink(current);
              if (href === null) return;
              if (href === '') {
                editor.chain().focus().unsetLink().run();
                return;
              }
              editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
            }}
            icon={<LinkIcon className="h-4 w-4" />}
          />
        ) : null}

        {onRequestImage ? (
          <ToolbarButton
            label={labels.image}
            onClick={async () => {
              const src = await onRequestImage();
              if (src) editor.chain().focus().setImage({ src }).run();
            }}
            icon={<ImageIcon className="h-4 w-4" />}
          />
        ) : null}

        <div className="ml-auto flex items-center gap-0.5">
          <ToolbarButton
            label={labels.undo}
            disabled={!editor.can().undo()}
            onClick={() => editor.chain().focus().undo().run()}
            icon={<Undo2 className="h-4 w-4" />}
          />
          <ToolbarButton
            label={labels.redo}
            disabled={!editor.can().redo()}
            onClick={() => editor.chain().focus().redo().run()}
            icon={<Redo2 className="h-4 w-4" />}
          />
        </div>
      </div>

      <EditorContent
        editor={editor as Editor}
        style={{ minHeight }}
        className="px-3 py-2.5 text-body text-fg"
      />
    </div>
  );
};

const Divider = () => <span aria-hidden className="mx-1 h-5 w-px bg-border" />;

const ToolbarButton = ({
  label,
  icon,
  onClick,
  active = false,
  disabled = false,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
}) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    aria-pressed={active}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      'rounded p-1.5 text-fg-secondary transition-colors',
      'hover:bg-surface hover:text-fg',
      active && 'bg-surface text-brand',
      disabled && 'cursor-not-allowed opacity-40 hover:bg-transparent',
    )}
  >
    {icon}
  </button>
);
